// ────────────────────────────────────────────────────────────────────────────
//  de.ts — Deutsche Übersetzungen
//  Default-Sprache. Englisch (en.ts) muss alle Keys exakt spiegeln.
// ────────────────────────────────────────────────────────────────────────────

export const de = {
  // ── Generisch ────────────────────────────────────────────────────────────
  common: {
    save:        'Speichern',
    cancel:      'Abbrechen',
    delete:      'Löschen',
    edit:        'Bearbeiten',
    change:      'Ändern',
    back:        'Zurück',
    loading:     'Lade…',
    saving:      'Speichere…',
    sending:     'Sende…',
    saved:       '✓ Gespeichert',
    error:       'Fehler',
    confirm:     'Bestätigen',
    close:       'Schließen',
    open:        'Öffnen',
    optional:    'optional',
    required:    'Pflicht',
    yes:         'Ja',
    no:          'Nein',
    or:          'oder',
    learnMore:   'Mehr erfahren',
  },

  // ── Auth ─────────────────────────────────────────────────────────────────
  auth: {
    login:           'Anmelden',
    logout:          'Abmelden',
    email:           'E-Mail',
    password:        'Passwort',
    currentPassword: 'Aktuelles Passwort',
    newPassword:     'Neues Passwort',
    repeatPassword:  'Passwort wiederholen',
    appleSignIn:     'Mit Apple anmelden',
    appleSoon:       'Web-Login mit Apple-ID wird gerade konfiguriert.',
    backToOverview:  '← Zurück zur Übersicht',
    welcome:         'Willkommen zurück',
    signInPrompt:    'Mit deinem CustoSoft-Account weiter.',
    orWithEmail:     'ODER MIT E-MAIL',
    forgotPassword:  'Passwort vergessen?',
    noAccount:       'Noch kein Account?',
    registerHint:    'Du kannst dich gratis in der iOS-/Mac-App registrieren.',
    minLength:       'Mindestens 8 Zeichen.',
    notMatching:     'Passwörter stimmen nicht überein.',
  },

  // ── Navigation ───────────────────────────────────────────────────────────
  nav: {
    home:      'Startseite',
    dashboard: 'Übersicht',
    bug:       'Bug melden',
    profile:   'Profil',
    admin:     'Admin öffnen',
    chat:      'Chat',
    punch:     'Stempeluhr',
    files:     'Akten',
    recruit:   'Bewerbungen',
    org:       'Orga',
    shop:      'Shop',
    payroll:   'Zeit',
    shifts:    'Schichtplan',
  },

  // ── Dashboard ────────────────────────────────────────────────────────────
  dashboard: {
    greetingNight:   'Gute Nacht',
    greetingMorning: 'Guten Morgen',
    greetingDay:     'Guten Tag',
    greetingEvening: 'Guten Abend',
    welcomeBack:     'Willkommen zurück bei CustoSoft.',
    glance:          'Auf einen Blick',
    areas:           'Webapp-Bereiche',
    soonPill:        'BALD',
    statThisWeek:    'Diese Woche',
    statUnread:      'Ungelesen',
    statRecruit:     'Bewerbungen',
    statStatus:      'Status',
    statAllOk:       'Alle Systeme laufen',
    statHintPunch:   'Stempelzeiten coming soon',
    statHintChat:    'Chat coming soon',
    statHintRecruit: 'Recruitment coming soon',
    fullFunction:    'Die volle Funktion gibt es bereits in den iOS- und Mac-Apps.',
    rolloutNote:     'Webapp-Bereiche werden Stück für Stück freigeschaltet.',
    bugBannerKicker: 'BETA-TESTER · DIREKT ZUM DEV',
    bugBannerTitle:  'Bug gefunden? Idee?',
    bugBannerText:   "Schreib's uns direkt — Screenshots, Videos, Logs hochladen. Geht <strong>sofort</strong> ans Dev-Team mit Mail-Notification.",
    wallClockTitle:  'Stempeluhr',
    wallClockHint:   'Live-Timer — synchron mit deiner iOS-App.',
    wallClockNotIn:  'Aktuell ausgestempelt',
    wallClockIn:     'Eingestempelt',
    wallClockOpen:   'Öffnen in der App',
    // Quick-Stats / Karten — vorher hartkodiert in Dashboard.tsx
    todayWorked:        'Heute',
    weekTotal:          'Diese Woche',
    chatUnreadOne:      '{n} ungelesen',
    chatUnreadOther:    '{n} ungelesen',
    expiringOne:        '1 Abo läuft bald ab',
    expiringOther:      '{n} Abos laufen bald ab',
    expiresTomorrow:    'morgen',
    expiresInDays:      'in {n} Tagen',
    timeYesterday:      'Gestern',
    activeCountSuffix:  'aktiv',
  },

  // ── Profile ──────────────────────────────────────────────────────────────
  profile: {
    title:           'Profil & Einstellungen',
    subtitle:        'Verwalte dein Konto, deine Erscheinung und deine Sicherheit.',
    accountTab:      'Konto',
    securityTab:     'Sicherheit',
    appearanceTab:   'Erscheinung',
    languageTab:     'Sprache',
    subscriptionTab: 'Abos',
    orgTab:          'Organisation',

    // Account
    accountTitle:    'Konto',
    accountSubtitle: 'Dein Name und Avatar — werden anderen Mitgliedern angezeigt.',
    firstName:       'Vorname',
    lastName:        'Nachname',
    fullName:        'Anzeigename',
    accountType:     'Kontotyp',
    typePrivate:     '🙂 Privat',
    typeOrg:         '🏢 Organisation',
    emailVerified:   '✓ E-Mail bestätigt',
    emailUnverified: '⚠ E-Mail unbestätigt',
    avatarChange:    '📷 Profilbild ändern',
    avatarRemove:    'Entfernen',
    avatarRemoveQ:   'Profilbild wirklich entfernen?',

    // Appearance
    appearanceTitle: 'Erscheinung',
    appearanceSub:   'Hell, dunkel oder dem System folgen.',
    themeLight:      'Hell',
    themeDark:       'Dunkel',
    themeSystem:     'System',
    appearanceNote:  'Im hellen Modus bekommt CustoSoft ein weißes Glas-Design im iOS-Stil.',

    // Sprache
    languageTitle:   'Sprache',
    languageSub:     'Auch für E-Mails (Verifizierung, Reset, Bug-Bestätigungen).',

    // Sicherheit
    securityTitle:   'Sicherheit',
    securitySub:     'Passwort und E-Mail-Adresse verwalten.',
    pwLabel:         'Passwort',
    pwLastChanged:   'Zuletzt geändert: unbekannt',
    pwChangeTitle:   'Passwort ändern',
    pwOk:            '✓ Passwort geändert.',
    emailAddress:    'E-Mail-Adresse',
    newEmailLabel:   'Neue E-Mail-Adresse',
    sendCode:        'Code senden',
    codeSentHint:    'Wir schicken einen 6-stelligen Code an die neue Adresse.',
    enterCode:       'Code aus der E-Mail eintragen',
    codeSentTo:      'Wir haben einen 6-stelligen Code an {email} gesendet.',
    codeInvalid:     'Code ungültig.',
    otherEmail:      'Andere E-Mail',

    // Subscription
    subsTitle:       'Aktive Abos & Käufe',
    subsSub:         'Welche Erweiterungen sind aktiv und wo wurden sie gekauft.',
    subsNone:        'Du hast noch keine aktive Erweiterung.',
    subsBuy:         'In der iOS-App freischalten →',
    subsExpiresAt:   'Läuft ab am {date}',
    subsActiveSince: 'Aktiv seit {date}',
    subsViaApple:    '🍎 Apple',
    subsViaWeb:      '🌐 Web',
    subsViaOrg:      '🏢 Über Organisation',
    subsCancelApple: 'Über App Store kündigen',
    subsCancelWeb:   'Kündigen',
    subsAppleHint:   'Apple-Abos kündigst du in den iOS-Einstellungen → Apple-ID → Abonnements.',

    // Organisation
    orgTitle:        'Organisation',
    orgSub:          'Welcher Firma gehörst du an.',
    orgNone:         'Aktuell keiner Organisation zugewiesen.',
    orgRole:         'Rolle',
    orgMembers:      'Mitglieder',
    orgLeave:        'Organisation verlassen',
    orgLeaveQ:       'Wirklich aus dieser Organisation austreten? Du verlierst Zugriff auf Org-Inhalte.',

    // Danger
    deleteTitle:     '⚠ Account löschen',
    deleteText:      'Wenn du dein Konto löschen möchtest, schreib uns kurz an {email}. Wir löschen es DSGVO-konform inklusive aller deiner Inhalte.',
  },

  // ── BugReport ────────────────────────────────────────────────────────────
  bug: {
    kicker:        'BUG-TRACKER · DIREKT ZUM DEV',
    title:         'Etwas läuft nicht?',
    sub:           "Beschreib's so genau wie möglich — Screenshot oder Screen-Recording hilft uns am meisten. Geht <strong>direkt</strong> an unser Dev-Team mit Mail-Notification.",
    fieldTitle:    'Was geht nicht?',
    titlePlace:    "Kurz und knackig — z.B. „Stempel-Button reagiert nicht",
    fieldDesc:     'Genauere Beschreibung',
    descPlace:     'Was hast du gemacht? Was ist passiert? Was sollte passieren? Reproduzierbar?',
    severity:      'Schweregrad',
    sevLow:        '🟢 Niedrig — Schönheitsfehler',
    sevMid:        '🟡 Mittel — Stört, aber umgehbar',
    sevHigh:       '🟠 Hoch — Feature kaputt',
    sevCrit:       '🔴 Kritisch — App unbenutzbar',
    platform:      'Plattform',
    platIos:       'iPhone',
    platIpad:      'iPad',
    platMac:       'Mac',
    platWeb:       'Webapp (hier)',
    platAll:       'Alle / mehrere',
    appVersion:    'App-Version (optional)',
    appVerPlace:   '1.7 (Build 8)',
    attach:        'Anhänge — Screenshot, Video, Datei (50 MB max pro Datei)',
    dropZone:      'Datei hierher ziehen oder klicken',
    dropTypes:     'Bilder · Videos · PDFs · Logs · Zips — bis 50 MB',
    submit:        '🐛 Report abschicken',
    titleRequired: 'Titel ist Pflicht.',
    waitUploads:   'Warte bis Uploads fertig sind.',
    fileTooBig:    '„{name}" ist größer als 50 MB.',
    reporterLabel: 'Reporter',
    uploaded:      '✓ Hochgeladen',
    uploading:     '⏳ Lade…',
    successTitle:  'Danke!',
    successText:   'Dein Report ist beim Dev-Team eingetrudelt — wir kümmern uns drum. Du kriegst keine automatische E-Mail-Antwort, aber wir melden uns falls wir Rückfragen haben.',
    another:       'Weiteren Bug melden',
  },

  // ── Shop ─────────────────────────────────────────────────────────────────
  shop: {
    title:           'Shop · Erweiterungen',
    sub:             'Alle Pakete & Add-Ons. Käufe gehen aktuell über die iOS-App — sicher per Apple-ID.',
    visualTitle:     'Live Activity',
    visualText:      'Dynamic Island, Lock-Screen-Banner, Mac-Menüleiste — dein Tag im Blick auch außerhalb der App.',
    cardSuffix:      'iOS / Mac App',
    dashTileSub:     'Erweiterungen & Add-Ons',
    productName: {
      PunchClock:          'Stempeluhr',
      TerminalMode:        'Wand-Stempeluhr',
      Recruitment:         'Bewerbungsmanager',
      ShiftPlanner:        'Schichtplaner',
      BusinessBasic:       'Business Basic',
      BusinessL:           'Business L',
      BusinessMAX:         'Business MAX',
      BusinessBasicYearly: 'Business Basic Jährlich',
      BusinessLYearly:     'Business L Jährlich',
      BusinessMAXYearly:   'Business MAX Jährlich',
      MoreSpace:           'Erweiterter Speicher',
    },
    productDesc: {
      PunchClock:          'Zeiterfassung für eine Person — 3,99 €/Monat. 1 Monat gratis testen nur in der iOS- oder Mac-App.',
      TerminalMode:        'iPad an die Wand: alle Mitarbeiter stempeln per PIN.',
      Recruitment:         'Stellen-Links erstellen, Bewerbungen sammeln.',
      ShiftPlanner:        'Arbeitszeiten planen, Schichten zuweisen, Team automatisch im Chat informieren.',
      BusinessBasic:       '10 Mitarbeiter-Slots · Stempeluhr · Akten · Chat',
      BusinessL:           '50 Mitarbeiter-Slots · Stempeluhr, Akten & Chat fürs ganze Team',
      BusinessBasicYearly: '10 Slots · bis zu 20 % Rabatt im Jahresabo',
      BusinessLYearly:     '50 Slots · bis zu 20 % Rabatt im Jahresabo',
    },
    iosOnlyHint:     'Apple-Pay-Zahlung folgt im Web. Bis dahin Karte / SEPA / PayPal über Stripe oder über die iOS- / Mac-App.',
    activeBadge:     '✓ Aktiv',
    monthly:         'Monatlich',
    yearly:          'Jährlich · bis zu 20 %',
    saveYearly:      'Spare bis zu 20 % im Jahresabo',
    free:            'Kostenlos',
    perMonth:        '/Monat',
    perYear:         '/Jahr',
    oneTime:         'Einmalig',
    sectionTeam:     'Für Teams',
    sectionTeamSub:  'Mitarbeiter-Slots, Stempeluhr, Akten und Chat — alles inklusive.',
    sectionSolo:     'Für Einzelpersonen',
    sectionSoloSub:  'Zeiterfassung als persönliches Tool — 1 Monat gratis testen, danach jederzeit kündbar.',
    launchSaleIndefinite: 'Launch-Sale · auf unbestimmte Zeit kostenlos',  // legacy, ungenutzt
    iosTrialOnly:    '1 Monat gratis · nur iOS / Mac',
    tryInApp:        'Gratis in der App testen',
    badge: {
      popular:      'BELIEBT',
      new:          'NEU',
      iosOnly:      'NUR iOS',
      upTo20:       'BIS ZU 20%',
      upTo20Short:  'BIS 20%',
    },
    sectionAddons:   'Add-Ons',
    sectionAddonsSub: 'Optionale Erweiterungen für dein Business-Paket.',
    sectionAddonsLockedSub: 'Sperrbar — benötigt mindestens Business Basic.',
    heroKicker:      'Erweiterungen · Abos · Add-Ons',
    heroSub:         'Alle Pakete im Überblick. Web-Checkout per Karte / SEPA / PayPal — Apple Pay folgt.',
    applePayTeaserTitle: 'Apple Pay im Web',
    applePayTeaserText:  'Wir bereiten den Express-Checkout per Apple Pay vor. Erscheint demnächst direkt auf jeder Karte.',
    applePaySoon:    'Bald verfügbar',
    openAppStore:    'App Store',
    appStoreKicker:  'Download im',
    lockedBadge:     'Gesperrt',
    webCheckoutSoon:      'Web-Checkout · in Kürze',
    webCheckoutSoonShort: 'Web-Checkout in Kürze',
    comingSoonTitle:      'Bald: Bezahlen direkt im Browser',
    comingSoonText:       'Wir arbeiten am Web-Checkout mit Apple Pay, Karte, SEPA und PayPal. Bis dahin geht\'s einfach über den App Store — gleicher Account, gleiches Abo, alles synchron.',
    addonNeedsBusiness: 'Business Basic erforderlich',
    addonNeedsBusinessHint: 'Add-Ons funktionieren nur in Verbindung mit einem Team-Paket.',
    orVia:           'oder',
    liveDemo:        'Live-Demo',
    demoCaption:     'Live-Timer, synchron mit deiner App',
    checkoutPay:     'Mit Karte / SEPA bezahlen',
    checkoutManage:  'Abo verwalten',
    checkoutLoading: 'Weiterleitung zu Stripe…',
    checkoutNoUrl:   'Stripe lieferte keine Checkout-URL.',
    checkoutError:   'Checkout fehlgeschlagen.',
    trustPrivacy:    { title: 'Datenschutz nach DSGVO', text: 'Server in der EU, alle Zahlungsdaten direkt bei Stripe — wir speichern keine Karten.' },
    trustNoLockIn:   { title: 'Keine Knebelverträge',    text: 'Monatsabos jederzeit kündbar, Jahresabo läuft einfach aus. Kein Pre-Pay-Lock.' },
    trustUpdates:    { title: 'Updates inklusive',       text: 'Alle neuen Features deines Pakets — automatisch, ohne Aufpreis.' },
    trustApple:      { title: 'Apple-Standard',          text: 'iOS / iPadOS / macOS native, mit Apple Sign-In, Push, Live Activities.' },
    sectionSoonWeb:  'Bald: Web-Checkout',
    buyOnApple:      ' In der App kaufen',
    openApp:         ' App öffnen',
    appStoreBadge:   'Im App Store',
    seeFeatures:     'Alle Features ansehen',
    teaserWebShop:   'Aktuell ist der Shop iOS-only. Stripe-Checkout für Web ist in Vorbereitung — als Org-Owner kannst du dann auch direkt im Browser bezahlen.',
    backToTop:       'Nach oben',
    badgeBest:       'BELIEBT',
    badgeRecommended: 'EMPFOHLEN',
    badgeFree:       'GRATIS',
    feat: {
      employees:     '{n} Mitarbeiter-Slots',
      punchclock:    'Stempeluhr für 1 Person',
      teamPunchclock: 'Stempeluhr fürs ganze Team',
      files:         'Mitarbeiter-Akten',
      chat:          'Team-Chat',
      recruitment:   'Bewerbungen sammeln & verwalten',
      terminal:      'Wand-Stempeluhr (iPad-Kiosk)',
      cancelable:    'Jederzeit kündbar',
      yearlyDiscount:'bis zu 20 % Rabatt zum Monatspreis',
      privateOnly:   'Volle Funktion — auch ohne Organisation',
      pinAuth:       'PIN-Login pro Mitarbeiter',
      kiosk:         'Vollbild-Modus',
      jobLinks:      'Öffentliche Stellen-Links',
      attachments:   'CV & Anhänge',
      payroll:       'Zeiterfassung & Auswertung',
      orgCreate:     'Eigene Organisation anlegen',
      moreSpace:     'Erweiterter Speicher (R2)',
      shiftsCreate:  'Schichten anlegen & bearbeiten',
      shiftsAssign:  'Mitarbeiter zuweisen, Rolle pro Schicht',
      shiftsChannel: 'Auto-Benachrichtigung im Schichtplan-Channel',
      shiftsView:    'Mitarbeiter sehen ihre Schichten auf allen Geräten',
      monthlyReactivation: 'Im Launch-Sale gratis · jeden Monat im Shop reaktivieren',  // legacy, ungenutzt
      firstMonthFree:      'Erster Monat gratis · nur in iOS / Mac',
    },
    bizIncludes:     'Business beinhaltet:',

    // Buy / Hero (Redesign)
    buy:                  'Jetzt freischalten',
    sectionTeamKicker:    'TEAMS',
    sectionSoloKicker:    'SOLO',
    sectionAddonsKicker:  'ADD-ONS',
    pillDsgvo:            'DSGVO · EU-Hosting',
    pillApple:            'Apple-native',
    pillCancel:           'Jederzeit kündbar',

    // ─── Filter-Tabs (neuer Shop-Layout) ───────────────────────────────
    filterAll:           'Alle',
    filterSolo:          'Solo',
    filterTeams:         'Teams',
    filterAddons:        'Add-Ons',
    filterCompare:       'Vergleich',

    // ─── App-Store-Banner (groß, oben) ────────────────────────────────
    appStoreNoticeKicker: 'NUR ÜBER DIE iOS-APP',
    appStoreNoticeTitle:  'Käufe gehen aktuell über die iOS-App',
    appStoreNoticeText:   'Der 1-Monat-Gratis-Test der Stempeluhr läuft per Apple-ID im App Store. Web-Checkout (Karte / SEPA / PayPal) folgt in Kürze.',

    // ─── PunchClock-Card-Hinweise ──────────────────────────────────────
    freeAppStoreOnly:    'Nur in der iOS-App',
    activateInApp:       'Gratis in der App aktivieren',

    // Spotlight (NEUES Produkt)
    spotlightKicker:      'NEU IM SHOP',
    spotlightSticker:     'NEU',
    spotlightPriceNote:   'einmaliger Kauf · keine Folgekosten',
    spotlightFeat1:       'Drag-and-Drop-Wochenplan auf Mac & iPad',
    spotlightFeat2:       'Mitarbeiter zuweisen, Rolle, Pause, Notizen',
    spotlightFeat3:       'Auto-Channel im Chat — alle sehen Änderungen sofort',
    spotlightFeat4:       'Mitarbeiter sehen ihren Plan auf jedem Apple-Gerät',
    spotlightSoonNote:    'In Kürze verfügbar — kein Kauf bisher möglich',
    spotlightSoonShort:   'BALD',
    soon:                 'Soon',
    soonBadge:            'Bald verfügbar',
    soonNote:             'Wir bauen gerade noch fleißig — Release-Termin folgt.',

    // Comparison-Strip
    compareKicker:        'VERGLEICH',
    compareTitle:         'Was ist wo dabei?',
    compareCol: {
      feature: 'Feature',
      solo:    'Solo',
      basic:   'Basic',
      large:   'L',
    },
    cmp: {
      punch:    'Stempeluhr',
      chat:     'Team-Chat',
      files:    'Mitarbeiter-Akten',
      payroll:  'Zeiterfassung',
      recruit:  'Bewerbungsmanager · Add-On',
      terminal: 'Wand-Stempeluhr · Add-On',
      shifts:   'Schichtplaner · Add-On',
      slots10:  '10 Mitarbeiter-Slots',
      slots50:  '50 Mitarbeiter-Slots',
    },
    compareFootnote:      'Add-Ons sind separat freischaltbar — sobald aktiv, gelten sie für die ganze Organisation.',

    // FAQ
    faqKicker: 'FAQ',
    faqTitle:  'Häufige Fragen',
    faq1: { q: 'Wie funktionieren die Mitarbeiter-Slots?', a: 'Jeder Mitarbeiter, der bei dir in der Org ist, belegt einen Slot. Business Basic bringt 10, Business L 50. Du kannst Mitglieder jederzeit deaktivieren — der Slot wird sofort wieder frei.' },
    faq2: { q: 'Kann ich monatlich kündigen?',             a: 'Ja. Monatsabos sind im App Store / über deinen Apple-Account jederzeit zum Monatsende kündbar. Jahresabos laufen einfach am Ende der Periode aus, ohne automatische Verlängerung wenn du das nicht möchtest.' },
    faq3: { q: 'Was passiert mit meinen Daten wenn ich kündige?', a: 'Deine Daten bleiben 30 Tage erhalten — du kannst sie als CSV exportieren oder das Abo wieder aktivieren. Nach 30 Tagen werden sie DSGVO-konform gelöscht.' },
    faq4: { q: 'Brauchen meine Mitarbeiter selbst ein Abo?', a: 'Nein. Sobald sie in deiner Organisation sind, bekommen sie alle freigeschalteten Features automatisch — Stempeluhr, Chat, Akten, Schichtplan. Du zahlst, das Team nutzt.' },
    faq5: { q: 'Wieso ist der Web-Checkout noch deaktiviert?', a: 'Wir testen aktuell die Apple-Pay-Integration. Bis zum Live-Switch geht der Kauf bequem über die iOS-App — gleicher Account, sofort verfügbar.' },

    // Footer-CTA
    footerKicker: 'BEREIT?',
    footerTitle:  'In 2 Minuten startklar.',
    footerSub:    'Lade die App, melde dich mit Apple an, leg deine Org an. Alles weitere erklärt sich von selbst.',
  },

  // ── Organisation ─────────────────────────────────────────────────────────
  org: {
    title:           'Deine Organisation',
    sub:             'Mitglieder, Rolle und Einladungen verwalten.',
    youBelong:       'Du gehörst zu',
    role:            'Rolle',
    members:         'Mitglieder',
    membersOf:       '{n} von {limit} Mitgliedern',
    activeExt:       'Aktive Erweiterungen',
    leaveBtn:        'Organisation verlassen',
    leaveConfirm:    'Wirklich aus „{name}" austreten? Du verlierst Zugriff auf alle Org-Inhalte (Chat, Akten, etc.).',

    // Tabs
    tabOverview:     'Übersicht',
    tabMembers:      'Mitglieder',
    tabPermissions:  'Berechtigungen',
    tabInvites:      'Einladungen',
    tabSettings:     'Einstellungen',
    permsOverview:   'Berechtigungs-Übersicht',
    permsOverviewSub: 'Sieh und ändere die Rechte aller Mitglieder auf einer Seite.',
    permsExpand:     'Aufklappen',
    permsCollapse:   'Einklappen',

    // Mitglieder-Verwaltung (Owner)
    promoteAdmin:    'Zum Admin machen',
    demoteAdmin:     'Admin-Rechte entziehen',
    removeMember:    'Aus Org entfernen',
    removeConfirm:   'Wirklich {name} aus der Organisation entfernen?',
    permsTitle:      'Berechtigungen',
    permsSub:        'Was {name} in der Organisation darf.',
    permClose:       'Schließen',
    permsSaved:      '✓ Berechtigungen gespeichert.',
    permLabels: {
      canManageMembers:          'Mitglieder verwalten',
      canManageInviteCodes:      'Einladungs-Codes verwalten',
      canCreateGroups:           'Gruppen-Chats erstellen',
      canManageFiles:            'Akten verwalten',
      canInviteToChats:          'Zu Chats einladen',
      canUseMoreSpace:           'Erweiterten Speicher nutzen',
      canViewSalaries:           'Gehälter einsehen',
      canManageEmployeeProfiles: 'Mitarbeiter-Profile verwalten',
      canManageOrgStructure:     'Org-Struktur verwalten',
      canUseRecruitment:         'Bewerbungsmanager nutzen',
      canManageRecruitment:      'Bewerbungsmanager verwalten',
    },

    // Einladungs-Codes
    inviteCodesTitle: 'Einladungs-Codes',
    inviteCodesSub:   'Teile einen Code mit jemandem, damit sie der Org beitreten können.',
    createCode:       'Neuen Code erstellen',
    codeMaxUses:      'Max. Nutzungen',
    codeNoLimit:      'unbegrenzt',
    codeExpires:      'Läuft ab',
    codeNoExpiry:     'Kein Ablaufdatum',
    codeExpiresAt:    'Läuft am {date} ab',
    codeUsed:         '{n}× genutzt',
    codeUsedOf:       '{n} / {max} Nutzungen',
    codeCopy:         'Kopieren',
    codeCopied:       '✓ Kopiert',
    codeShare:        'Teilen',
    codeDelete:       'Löschen',
    codeDeleteConfirm: 'Diesen Einladungs-Code wirklich löschen?',
    codeFormUses:     'Anzahl Nutzungen',
    codeFormUsesAny:  'Unbegrenzt (∞)',
    codeFormUses1:    'Einmal',
    codeFormUsesN:    '{n}×',
    codeFormExpiry:   'Gültig für',
    codeFormExpAny:   'Niemals ablaufen',
    codeFormExp1d:    '1 Tag',
    codeFormExp7d:    '7 Tage',
    codeFormExp30d:   '30 Tage',
    codeFormSubmit:   'Code generieren',
    codeEmpty:        'Noch kein Einladungs-Code erstellt.',

    // E-Mail-Einladung
    inviteByEmailTitle: 'Per E-Mail einladen',
    inviteByEmailSub:   'Wir schicken eine schöne E-Mail mit Beitrittslink.',
    inviteEmailPh:      'name@firma.de',
    inviteMessagePh:    'Persönliche Nachricht (optional)',
    inviteSend:         'Einladung verschicken',
    inviteSentOk:       '✓ Einladung verschickt an {email}',

    // Einstellungen
    settingsTitle:    'Organisations-Einstellungen',
    settingsSub:      'Name, Logo, Auflösung.',
    nameField:        'Name der Organisation',
    saveBtn:          'Speichern',
    dangerTitle:      '⚠ Org auflösen',
    dangerText:       'Löscht die Organisation komplett. Alle Akten werden archiviert, alle Chats und Mitglieder werden entfernt. Diese Aktion ist endgültig.',
    deleteBtn:        'Organisation auflösen',
    deleteConfirm:    'Wirklich „{name}" auflösen? Alle Daten gehen verloren!',
    deletedOk:        'Organisation wurde aufgelöst.',

    // Mitglieder-Card
    memberSince:     'Mitglied seit {date}',

    // Beitritt (kein Org)
    joinTitle:        'Du gehörst noch keiner Organisation an',
    joinSub:          'Trete einer bestehenden Organisation bei oder warte auf eine Einladung.',
    joinCodePlaceholder: 'Einladungs-Code eingeben',
    joinPreview:      'Code prüfen',
    joinConfirm:      '„{name}" beitreten',
    joinCancel:       'Abbrechen',
    joinPreviewLabel: 'Wenn du beitrittst, gehörst du zu',
    joinOwnerLabel:   'Inhaber',
    joinMemberCount:  '{n} Mitglieder',
    joinSuccess:      '✓ Du bist nun Mitglied von „{name}".',

    // Erstellung
    createTitle:      'Eigene Organisation erstellen',
    createReady:      'Deine Lizenz erlaubt das Erstellen einer Organisation. Wähle einen Namen und du kannst dein Team einladen.',
    createNeedsBiz:   'Um eine eigene Organisation zu erstellen brauchst du ein Business-Paket (Basic oder L, monatlich oder jährlich), das All-in-One-Bundle oder eine der Org-Erweiterungen (Bewerbungsmanager, MoreSpace). Im Shop freischalten — du bekommst: Org-Verwaltung, Stempeluhr für alle, Zeiterfassung & Auswertung, Mitarbeiter-Akten und Team-Chat.',
    createGoShop:     'Zum Shop',
    createOpenApp:    'In der App öffnen',
    createNamePlaceholder: 'Name der Organisation',
    createBtn:        'Organisation erstellen',
    createSuccess:    '✓ Deine Organisation „{name}" ist startklar.',
  },

  // ── Chat ─────────────────────────────────────────────────────────────────
  chat: {
    title:           'Chat',
    sub:             'Direkt-Chat & Gruppen-Channels deiner Organisation.',
    listTitle:       'Konversationen',
    empty:           'Noch keine Chats. Starte einen Direkt-Chat mit einem Org-Mitglied.',
    newDM:           'Neue Nachricht',
    newDMSelect:     'Wähle ein Mitglied:',
    backToList:      '← Zurück',
    placeholderInput: 'Nachricht schreiben…',
    sendBtn:         'Senden',
    typeDM:          'Direkt',
    typeGroup:       'Gruppe',
    typeSupport:     'Support',
    nothingYet:      'Schreib die erste Nachricht in diesem Chat.',
    sentBy:          'Du',
    today:           'Heute',
    yesterday:       'Gestern',
    unreadBadge:     '{n}',
    selectChat:      'Wähle einen Chat aus der Liste',
    selectChatSub:   'oder starte einen neuen Direkt-Chat mit einem Org-Mitglied.',
    deleteAction:    'Chat löschen',
    deleteTitle:     'Chat löschen?',
    deleteText:      '„{title}" wird aus deiner Liste entfernt. Andere Mitglieder behalten den Chat weiter — du kannst dich später neu hinzufügen lassen.',

    // No-Org-Lock
    lockedTitle:     'Chat ist organisationsgebunden',
    lockedSub:       'Du brauchst eine Organisation um chatten zu können. Trete bei oder lass dich einladen.',
    lockedJoin:      'Zur Organisation →',

    // Datei-Anhang
    attachAction:        'Datei anhängen',
    attachPlaceholder:   'Bild, PDF oder Dokument…',
    attachUploading:     'Lädt hoch…',
    attachRemove:        'Anhang entfernen',
    attachOpen:          'Öffnen',
    attachDownload:      'Herunterladen',
    attachClose:         'Schließen',
    attachTooLarge:      'Datei ist zu groß. Maximal {max} pro Anhang.',
    attachTypeBlocked:   'Dateityp nicht erlaubt. Nur Bilder und Dokumente (PDF, DOC, XLS, PPT, TXT…) sind erlaubt.',
    attachUploadFailed:  'Upload fehlgeschlagen: {error}',
  },

  // ── Zeiterfassung & Auswertung ────────────────────────────────────────
  payroll: {
    title:           'Zeiterfassung & Auswertung',
    sub:             'Wer hat wie lange gearbeitet — auf einen Blick.',
    tabAnalysis:     'Zeitanalyse',
    tabPayroll:      'Zeiterfassung',
    tabEmployees:    'Mitarbeiter',

    rangeWeek:       'Diese Woche',
    rangeMonth:      'Dieser Monat',
    rangeQuarter:    'Quartal',
    rangeYear:       'Jahr',

    statTotal:       'Gesamtarbeitszeit',
    statEmployees:   'Mitarbeiter',
    statAvgPerEmp:   'Ø pro Mitarbeiter',
    statPauseTotal:  'Gesamt-Pausen',
    statWorkDays:    'Arbeitstage',

    perMemberTitle:  'Pro Mitarbeiter',
    perMemberEmpty:  'Keine Daten im gewählten Zeitraum.',
    memberDetailTitle: 'Stempelzeiten von {name}',
    memberDetailSub:   'Klick auf einen Eintrag um ihn zu bearbeiten — alle Änderungen werden im Audit-Log festgehalten.',
    memberRecordsEmpty: 'Keine Stempelzeiten in den letzten 30 Tagen.',
    memberOpenRecords: 'Zeiten ansehen & bearbeiten',
    colName:         'Name',
    colDays:         'Arbeitstage',
    colWorked:       'Arbeitszeit',
    colPause:        'Pause',
    colHourly:       'Stundensatz',
    colGross:        'Brutto',
    colHours:        'Stunden',
    colStatus:       'Status',
    statusLive:      'Live',
    statusOff:       'Off',

    payrollHint:     'Berechnung: Arbeitsstunden × Stundensatz. Werte ohne hinterlegten Stundensatz werden als „—" angezeigt.',
    csvExport:       'CSV-Export',
    csvFile:         'lohnabrechnung-{period}.csv',

    employeesEmpty:  'Noch keine Mitarbeiter mit Profil.',
    empSetSalary:    'Stammdaten bearbeiten',
    empPosition:     'Position',
    empDepartment:   'Abteilung',
    empWeeklyHours:  'Wochenstunden',
    empHireDate:     'Eintrittsdatum',
    empNotes:        'Notizen (intern)',
    empSave:         'Speichern',
    empSaved:        '✓ Gespeichert',
    empSalaryHidden: 'Gehälter werden nur dem Inhaber und Mitgliedern mit „Gehälter sehen"-Berechtigung angezeigt.',

    locked:          'Zeiterfassung & Auswertung ist Teil von Business',
    lockedSub:       'Zeiterfassung & Auswertung sind in jedem Business-Paket (Basic oder L, monatlich oder jährlich) automatisch enthalten — keine eigene Erweiterung nötig. Schalte Business im Shop frei und es ist sofort dabei.',
    lockedShop:      'Business im Shop ansehen',
    forbidden:       'Keine Berechtigung',
    forbiddenSub:    'Nur Inhaber, Admins oder Mitglieder mit „Gehälter sehen"-Berechtigung können diese Daten einsehen.',
  },

  // ── Schichtplaner ────────────────────────────────────────────────────────
  shifts: {
    kicker:        'NEU · ARBEITSZEITEN',
    title:         'Schichtplaner',
    sub:           'Wochenplan, Mitarbeiter zuweisen, automatisch im Chat veröffentlichen — alle sehen ihren Plan auf jedem Apple-Gerät.',
    tabMy:         'Mein Plan',
    tabManage:     'Schichten verwalten',

    // My Plan
    myEmpty:       'Noch keine Schichten geplant.',
    myEmptySub:    'Sobald dein Team den ersten Plan veröffentlicht, erscheint er hier — und du bekommst eine Nachricht im Schichtplan-Channel.',
    myUpcoming:    'Anstehend',
    myPast:        'Vergangen',
    assigned:      'Eingeteilt',
    // Legacy (Accept/Decline-Workflow entfernt — Mitarbeiter werden nur informiert)
    accepted:      '✓ Angenommen',
    declined:      '✗ Abgelehnt',
    pending:       '◔ Offen',
    accept:        'Annehmen',
    decline:       'Ablehnen',
    declineReasonAsk: 'Grund (optional):',
    today:         'Heute',
    tomorrow:      'Morgen',

    // Manage
    thisWeek:      'Diese Woche',
    add:           'Schicht anlegen',
    noPermTitle:   'Du darfst keine Schichten bearbeiten',
    noPermSub:     'Frag deinen Inhaber oder einen Admin um die Berechtigung „Schichten verwalten".',

    // Editor
    newTitle:      'Neue Schicht',
    editTitle:     'Schicht bearbeiten',
    fieldTitle:    'Titel',
    fieldTitlePh:  'z.B. Frühschicht Backstube',
    fieldStart:    'Start',
    fieldEnd:      'Ende',
    fieldLocation: 'Ort',
    fieldLocationPh: 'z.B. Filiale Mitte',
    fieldRole:     'Rolle (optional)',
    fieldRolePh:   'z.B. Kasse, Service',
    fieldBreak:    'Pause (Minuten)',
    fieldColor:    'Farbe',
    fieldDesc:     'Beschreibung',
    fieldDescPh:   'Was ist zu tun? Besonderheiten? Übergabe?',
    fieldAssign:   'Mitarbeiter zuweisen',
    noMembers:     'Keine Mitarbeiter in der Org.',
    publish:       'Freigeben & im Channel posten',
    deleteConfirm: 'Schicht wirklich löschen? Alle Zuweisungen werden entfernt.',

    // Upsell
    upsellTitle:   'Schichtplaner ist ein Add-On',
    upsellSub:     'Mit dem Add-On „Schichtplaner" (9,99 € einmalig) kannst du Schichten für dein Team anlegen, zuweisen und automatisch im Chat veröffentlichen.',
    upsellCta:     'Im Shop ansehen',

    // No-Org-Lock
    noOrgTitle:    'Schichtplan ist organisationsgebunden',
    noOrgSub:      'Du brauchst eine Organisation um Schichten zu sehen oder zu planen.',
    noOrgCta:      'Zur Organisation →',
  },

  // ── Stempeluhr / Punch ───────────────────────────────────────────────────
  punch: {
    title:           'Stempeluhr',
    sub:             'Live-Timer mit Pausen-Tracking und Wochenstatistik.',
    statusOut:       'Ausgestempelt',
    statusIn:        'Eingestempelt',
    statusPaused:    'In Pause',
    clockIn:         '▶ Einstempeln',
    clockOut:        '■ Ausstempeln',
    pauseStart:      '⏸ Pause',
    pauseEnd:        '▶ Weiter',
    sinceLabel:      'seit {time}',
    todayWorked:     'Heute',
    todayPaused:     'Pause heute',
    weekTotal:       'Diese Woche',
    weekAvg:         'Ø pro Tag',
    notesLabel:      'Notiz',
    notesPh:         'Notiz hinzufügen (optional)…',
    saveNote:        'Notiz speichern',
    historyTitle:    'Verlauf',
    historyEmpty:    'Noch keine Stempelzeiten.',
    historyToday:    'Heute',
    historyYesterday:'Gestern',
    deleteRecord:    'Eintrag löschen',
    deleteConfirm:   'Diesen Stempel-Eintrag wirklich löschen?',
    durationFmt:     '{h}h {m}min',
    pauseFmt:        '{m}min Pause',
    runningFmt:      'läuft seit {h}h {m}min',
    locked:          'Stempeluhr nicht freigeschaltet',
    lockedSub:       'Du brauchst die Stempeluhr-Erweiterung. Du findest sie im Shop oder im Business-Paket deiner Organisation.',
    lockedShop:      'Im Shop ansehen',
    rangeWeek:       'Woche',
    rangeMonth:      'Monat',
    rangeAll:        'Alle',
    pickRange:       'Zeitraum',

    // ── Reset & Archiv ──────────────────────────────────────────────────
    resetTitle:      'Stempeluhr zurücksetzen',
    resetSub:        'Archiviert alle deine Stempelzeiten als persönliches Profil — du fängst danach bei null an.',
    resetBtn:        'Stempeluhr zurücksetzen',
    resetConfirmTitle: 'Wirklich zurücksetzen?',
    resetConfirmText:  'Alle deine Stempelzeiten werden in ein Archiv verschoben. Du kannst dort weiter draufschauen, aber nicht mehr bearbeiten. Danach startet deine Stempeluhr bei null.',
    resetConfirmDo:    'Zurücksetzen',
    resetDoneTitle:    '✓ Zurückgesetzt',
    resetDoneN:        '{n} Einträge archiviert',
    resetBlockedOrg:   'In einer Organisation kannst du deine Stempeluhr nicht zurücksetzen. Verlasse erst die Organisation — deine Zeiten werden dann automatisch archiviert.',

    archivesTitle:   'Archivierte Stempelzeit-Profile',
    archivesEmpty:   'Noch keine archivierten Profile.',
    archiveOpen:     'Öffnen',
    archiveBack:     '← Zurück',
    archiveMeta:     'Archiviert am {date}',
    archiveReason: {
      user_reset:  'Selbst zurückgesetzt',
      org_leave:   'Beim Org-Verlassen',
      org_remove:  'Aus Org entfernt',
      org_delete:  'Org wurde aufgelöst',
    },
    archiveSpan:     '{from} – {to}',
    archiveTotal:    'Gesamtarbeitszeit',
    archiveEntries:  '{n} Einträge',
    archivePause:    'Pausen gesamt',
    archiveReadOnly: 'Diese Zeiten sind archiviert — nicht mehr veränderbar.',

    // ── Edit-Sheet ──────────────────────────────────────────────────────
    editTitle:       'Stempel-Eintrag bearbeiten',
    editSub:         'Korrigiere Beginn, Ende, Pause oder Notiz.',
    editClockIn:     'Beginn',
    editClockOut:    'Ende',
    editPauseMin:    'Pause (Minuten)',
    editNote:        'Notiz',
    editSave:        'Speichern',
    editCancel:      'Abbrechen',
    editSaved:       '✓ Gespeichert',
    editEndBeforeStart: 'Ende darf nicht vor dem Beginn liegen.',
    editLockedNoPerm: 'In einer Organisation kannst du Stempelzeiten nur mit „Mitarbeiterprofile verwalten"-Berechtigung bearbeiten.',
    editManualBadge: 'Manuell',
    editBtn:         'Bearbeiten',
    deleteBtn:       'Löschen',

    // ── Audit-Log ───────────────────────────────────────────────────────
    auditTitle:      'Änderungs-Verlauf',
    auditEmpty:      'Noch keine Änderungen.',
    auditBy:         'von {editor}',
    auditWhen:       'am {date}',
    auditField: {
      clock_in:      'Beginn',
      clock_out:     'Ende',
      pause_seconds: 'Pause',
      note:          'Notiz',
      is_manual:     'Manuell-Flag',
    },
    auditFromTo:     'von {from} → {to}',
    auditUnset:      '(leer)',
  },

  // ── Bewerbungssystem / Recruitment ───────────────────────────────────────
  recruit: {
    title:           'Bewerbungsmanager',
    sub:             'Stellen-Links erstellen, Bewerbungen empfangen.',
    tabLinks:        'Stellen',
    tabApplications: 'Bewerbungen',

    // Links
    linksEmpty:      'Noch keine Stellen-Links erstellt.',
    newLink:         '+ Neue Stelle',
    linkTitleField:  'Titel der Stelle',
    linkTitlePh:     'z.B. „Bäckerei-Verkauf (Teilzeit)"',
    linkDescField:   'Beschreibung',
    linkDescPh:      'Aufgaben, Anforderungen, Arbeitszeiten …',
    linkPositionField: 'Position (optional)',
    linkExpiry:      'Gültig bis',
    linkCreate:      'Stelle erstellen',
    linkCreated:     '✓ Stellen-Link erstellt',
    linkPublic:      'Öffentlicher Link',
    linkCopy:        'Kopieren',
    linkCopied:      '✓ Kopiert',
    linkActive:      'Aktiv',
    linkInactive:    'Inaktiv',
    linkUsedCount:   '{n} Bewerbung(en)',
    linkActions:     'Aktionen',
    linkEdit:        'Bearbeiten',
    linkDeactivate:  'Deaktivieren',
    linkActivate:    'Aktivieren',
    linkDelete:      'Löschen',
    linkDeleteConfirm: '„{title}" wirklich löschen? Bestehende Bewerbungen bleiben erhalten.',

    // Applications
    appsEmpty:       'Noch keine Bewerbungen eingegangen.',
    appsAll:         'Alle',
    appsNew:         'Neu',
    appsViewed:      'Gesichtet',
    appsContacted:   'Kontaktiert',
    appsAccepted:    'Angenommen',
    appsRejected:    'Abgelehnt',
    appName:         'Name',
    appEmail:        'E-Mail',
    appPhone:        'Telefon',
    appCoverLetter:  'Anschreiben',
    appAttachments:  'Anhänge',
    appNoAttach:     '(keine Anhänge)',
    appNotes:        'Interne Notizen',
    appNotesPh:      'Eigene Notizen für das Team (nur intern sichtbar) …',
    appAssignTo:     'Zuständig',
    appNotAssigned:  '(niemand)',
    appAppliedAt:    'Eingegangen am',
    appUpdate:       'Aktualisieren',
    appUpdated:      '✓ Aktualisiert',
    appBack:         '← Zurück',

    // Lock
    locked:          'Bewerbungsmanager nicht freigeschaltet',
    lockedSub:       'Du brauchst die Recruitment-Erweiterung oder ein Business-Paket. Im Shop kannst du das freischalten.',
    lockedShop:      'Im Shop ansehen',

    // Bewerber-Einstieg statt Lock-Screen
    haveCodeTitle:   'Hast du einen Bewerbungs-Code?',
    haveCodeSub:     'Trag ihn hier ein, um direkt zur Stellenanzeige zu kommen und dich zu bewerben.',
    haveCodeBtn:     'Zur Stelle →',
    haveCodeOpen:    'Bewerbungsseite öffnen',
    recruiterHint:   'Du willst selbst Bewerbungen empfangen?',
    recruiterShop:   'Im Shop freischalten →',
    recruiterOrg:    'Org beitreten oder erstellen →',

    // ── Öffentliche Bewerbungs-Seite (/apply/:code) ────────────────────
    applyKicker:     'Bewerbung',
    applyAt:         'bei {org}',
    applyForm:       'Deine Bewerbung',
    applyFirstName:  'Vorname',
    applyLastName:   'Nachname',
    applyEmail:      'E-Mail',
    applyPhone:      'Telefon (optional)',
    applyCover:      'Anschreiben',
    applyCoverPh:    'Erzähl uns kurz, warum du die Stelle spannend findest …',
    applyAttachTitle: 'Anhänge (Lebenslauf, Zeugnisse, …)',
    applyAttachHint: 'PDF, Word, Bild — bis 25 MB pro Datei',
    applyDropHere:   'Dateien hierher ziehen oder klicken',
    applyAttachOk:   '✓ Hochgeladen',
    applyAttachFail: '❌ Fehler',
    applyAttachLoad: '⏳ Lade…',
    applySubmit:     'Bewerbung abschicken',
    applySubmitting: 'Sende…',
    applyConsent:    'Mit dem Absenden bestätigst du, dass deine Daten zur Bearbeitung der Bewerbung gespeichert werden. Datenschutz: ',
    applyConsentLink: 'Datenschutzerklärung',
    applyDoneTitle:  'Vielen Dank!',
    applyDoneText:   'Deine Bewerbung ist bei {org} eingegangen — wir melden uns bei dir.',
    applyDoneAnother:'Weitere Bewerbung',
    applyNotFound:   'Stellenanzeige nicht gefunden',
    applyNotFoundSub:'Der Bewerbungs-Code ist ungültig oder die Stelle ist nicht mehr aktiv.',
    applyExpired:    'Stellenanzeige abgelaufen',
    applyExpiredSub: 'Diese Bewerbung ist nicht mehr offen.',
    applyCodeInputTitle: 'Bewerbungs-Code eingeben',
    applyCodeInputSub: 'Hast du einen Bewerbungs-Code von einer Firma bekommen? Gib ihn hier ein.',
    applyCodeInputPh: 'Code',
    applyCodeInputBtn: 'Stelle ansehen',
    applyCodeInputErr: 'Code unbekannt oder Stelle nicht mehr aktiv.',

    // No-Org-Lock (Recruitment ist auch Org-bound)
    noOrgTitle:      'Bewerbungen sind organisationsgebunden',
    noOrgSub:        'Du brauchst eine Organisation. Trete bei oder erstelle eine.',
    noOrgGoOrg:      'Zur Organisation →',
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  footer: {
    impressum:   'Impressum',
    datenschutz: 'Datenschutz',
    agb:         'AGB',
    contact:     'Kontakt',
    madeIn:      'Made with 🔥 in Germany',
  },
}

export type Translation = typeof de
